import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Copy, Check, LogOut, Link as LinkIcon, CircleDollarSign,
  MousePointerClick, Users, Clock, CheckCircle, BookOpen, ThumbsUp, ThumbsDown,
  Download, Package, FileText, Image as ImageIcon, AlertTriangle,
  Globe, Map, TrendingUp, Send,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosP = () => {
  const token = localStorage.getItem("lf_promoter_session") || "";
  return axios.create({
    withCredentials: true,
    headers: token ? { "X-Promoter-Session": token } : {},
  });
};

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Stat({ icon: Icon, label, value, color = "zinc" }) {
  return (
    <div className={`bg-white border border-${color}-200 rounded-2xl p-4`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-zinc-500">{label}</div>
          <div className={`text-2xl font-extrabold text-${color}-700 mt-1`}>{value}</div>
        </div>
        <Icon size={16} className={`text-${color}-500`} />
      </div>
    </div>
  );
}

function Guidelines({ open, onClose, guide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center px-4 py-10 overflow-y-auto" onClick={onClose} data-testid="promoter-guidelines-modal">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full my-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={18} />
          <h3 className="font-bold text-zinc-950 text-lg">How to promote FastPrototype.ai</h3>
        </div>
        {guide?.intro && <p className="text-sm text-zinc-700 mb-5 leading-relaxed whitespace-pre-wrap">{guide.intro}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="guide-dos">
            <div className="flex items-center gap-2 mb-3">
              <ThumbsUp size={14} className="text-emerald-700" />
              <span className="font-bold text-emerald-900 text-sm">Please do</span>
            </div>
            <ul className="space-y-2 text-sm text-emerald-900">
              {(guide?.dos || []).length === 0 && <li className="text-emerald-700/60 italic">No rules yet.</li>}
              {(guide?.dos || []).map((d) => <li key={`do-${d}`} className="flex items-start gap-2"><CheckCircle size={12} className="mt-1 flex-shrink-0" />{d}</li>)}
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="guide-donts">
            <div className="flex items-center gap-2 mb-3">
              <ThumbsDown size={14} className="text-red-700" />
              <span className="font-bold text-red-900 text-sm">Please don't</span>
            </div>
            <ul className="space-y-2 text-sm text-red-900">
              {(guide?.donts || []).length === 0 && <li className="text-red-700/60 italic">No rules yet.</li>}
              {(guide?.donts || []).map((d) => <li key={`dont-${d}`} className="flex items-start gap-2"><AlertTriangle size={12} className="mt-1 flex-shrink-0" />{d}</li>)}
            </ul>
          </div>
        </div>
        {guide?.terms && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-700 whitespace-pre-wrap" data-testid="guide-terms">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Commission terms</div>
            {guide.terms}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PromoterDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [guide, setGuide] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("lf_promoter_session");
    if (!token) { navigate("/promoter/login"); return; }
    Promise.all([
      axiosP().get(`${API}/promoter/stats`),
      axiosP().get(`${API}/promoter/guidelines`),
      axiosP().get(`${API}/promoter/materials`),
      axiosP().get(`${API}/promoter/leads`),
      axiosP().get(`${API}/promoter/traffic?days=30`),
      axiosP().get(`${API}/promoter/payout-requests`),
    ]).then(([s, g, m, l, t, pr]) => {
      setData(s.data); setGuide(g.data); setMaterials(m.data || []);
      setLeads(l.data || []); setTraffic(t.data || null); setPayoutRequests(pr.data || []);
    })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.removeItem("lf_promoter_session"); navigate("/promoter/login"); return; }
        setError(err.response?.data?.detail || "Could not load dashboard");
      }).finally(() => setLoading(false));
  }, [navigate]);

  const logout = async () => {
    try { await axiosP().post(`${API}/promoter/auth/logout`); } catch (err) { console.warn("Promoter logout call failed (continuing client-side):", err); }
    localStorage.removeItem("lf_promoter_session");
    navigate("/promoter/login");
  };

  const downloadMaterial = async (m) => {
    if (!m.file_id) return;
    try {
      const res = await axiosP().get(`${API}/promoter/materials/${m.material_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m.file_filename || m.title; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  const requestPayout = async () => {
    if (requestingPayout) return;
    setRequestingPayout(true);
    try {
      const res = await axiosP().post(`${API}/promoter/payout-request`);
      const pr = await axiosP().get(`${API}/promoter/payout-requests`);
      setPayoutRequests(pr.data || []);
      if (res.data?.duplicate) {
        alert("You already have an open payout request — we're on it.");
      } else {
        alert(`Payout request submitted for ${fmt(res.data?.amount_usd || 0)}. We'll settle within 5 business days.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Could not submit payout request");
    } finally { setRequestingPayout(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="promoter-dashboard">
      <Loader2 size={28} className="animate-spin text-zinc-400" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="text-center"><AlertTriangle size={28} className="text-red-500 mx-auto mb-3" /><p className="text-sm text-zinc-700">{error}</p></div>
    </div>
  );

  const { promoter, stats, commissions } = data;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://fastprototype.ai";
  const link = `${origin}/?ref=${promoter.code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const hasPendingRequest = payoutRequests.some(r => r.status === "requested");
  const canRequestPayout = (stats.pending_commission_usd || 0) > 0 && !hasPendingRequest;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="promoter-dashboard">
      <header className="bg-white border-b border-zinc-200 px-4 sm:px-6 py-4 flex items-center gap-3">
        <BrandLogo variant="mark" size={32} />
        <div>
          <div className="font-bold text-zinc-900 text-sm" data-testid="promoter-name">{promoter.name}</div>
          <div className="text-xs text-zinc-500">{promoter.email} · {Math.round((promoter.commission_rate || 0.15) * 100)}% commission</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setOpenGuide(true)} data-testid="btn-open-guidelines" className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-full">
            <BookOpen size={12} /> Guidelines
          </button>
          <button onClick={logout} data-testid="btn-promoter-logout" className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 px-3 py-1.5 rounded-full">
            <LogOut size={12} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="bg-gradient-to-br from-zinc-950 to-zinc-800 rounded-3xl p-6 text-white" data-testid="tracking-link-card">
          <div className="flex items-center gap-2 mb-2"><LinkIcon size={16} /><span className="text-xs font-bold uppercase tracking-wider text-zinc-300">Your tracking link</span></div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <div className="flex-1 min-w-0 font-mono text-sm bg-white/10 px-3 py-2 rounded-xl truncate">{link}</div>
            <button data-testid="btn-copy-link" onClick={copy} className="inline-flex items-center gap-1.5 bg-white text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full hover:bg-zinc-100">
              {copied ? <><Check size={12} className="text-emerald-600" /> Copied</> : <><Copy size={12} /> Copy link</>}
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-3">
            Share this URL in articles, videos, newsletters — anywhere. Every paid project from a first-touch visitor earns you {Math.round((promoter.commission_rate || 0.15) * 100)}% for 90 days.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={MousePointerClick} label="Clicks" value={stats.clicks} />
          <Stat icon={Users} label="Attributed projects" value={stats.attributed_projects} />
          <Stat icon={Clock} label="Pending commission" value={fmt(stats.pending_commission_usd)} color="amber" />
          <Stat icon={CircleDollarSign} label="Paid to date" value={fmt(stats.paid_commission_usd)} color="emerald" />
        </div>

        {/* Leads */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="leads-card">
          <h2 className="font-bold text-zinc-950 text-base mb-1 flex items-center gap-2">
            <Users size={16} /> Your leads
          </h2>
          <p className="text-xs text-zinc-500 mb-3">Projects attributed to your link. Client details are masked for privacy.</p>
          {leads.length === 0 ? (
            <p className="text-sm text-zinc-500 py-3">No leads yet — share your tracking link above to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-100 text-zinc-500">
                  {["Date", "Client", "Industry", "Package", "Status"].map(h => <th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wide">{h}</th>)}
                </tr></thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.project_id} className="border-b border-zinc-50" data-testid={`lead-${l.project_id}`}>
                      <td className="py-2 px-3 text-zinc-600">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                      <td className="py-2 px-3 font-mono text-xs text-zinc-700">{l.client_initials}</td>
                      <td className="py-2 px-3 text-zinc-700 capitalize">{l.industry || "—"}</td>
                      <td className="py-2 px-3 text-zinc-600">{l.selected_delivery_weeks ? `${l.selected_delivery_weeks} wk` : "—"}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${l.status === "delivered" ? "bg-emerald-100 text-emerald-700" : l.status === "rejected" ? "bg-red-100 text-red-700" : l.status === "awaiting_deposit" ? "bg-zinc-100 text-zinc-600" : "bg-amber-100 text-amber-700"}`}>
                          {l.status_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Traffic sources */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="traffic-card">
          <h2 className="font-bold text-zinc-950 text-base mb-1 flex items-center gap-2">
            <TrendingUp size={16} /> Traffic &amp; sources <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full ml-2">Last 30 days</span>
          </h2>
          <p className="text-xs text-zinc-500 mb-4">Where your clicks are coming from, and which landing pages convert.</p>
          {!traffic || (traffic.totals?.clicks_window || 0) === 0 ? (
            <p className="text-sm text-zinc-500 py-3">No traffic in the last 30 days yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Globe size={12} /> Top referrers</div>
                <ul className="space-y-1.5" data-testid="top-referrers">
                  {(traffic.top_referrers || []).slice(0, 8).map(r => (
                    <li key={r.source} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-zinc-800">{r.source}</span>
                      <span className="font-bold text-zinc-950 tabular-nums">{r.clicks}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Map size={12} /> Top landing pages</div>
                <ul className="space-y-1.5" data-testid="top-landing">
                  {(traffic.top_landing || []).slice(0, 8).map(p => (
                    <li key={p.landing_path} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate font-mono text-xs text-zinc-700">{p.landing_path}</span>
                      <span className="font-bold text-zinc-950 tabular-nums">{p.clicks}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {(traffic.daily || []).length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Daily clicks</div>
                  <div className="flex items-end gap-1 h-20" data-testid="daily-chart">
                    {(() => {
                      const max = Math.max(1, ...traffic.daily.map(d => d.clicks));
                      return traffic.daily.map(d => (
                        <div key={d.day} title={`${d.day}: ${d.clicks}`}
                          className="flex-1 bg-gradient-to-t from-emerald-500 to-sky-400 rounded-t"
                          style={{ height: `${Math.max(4, (d.clicks / max) * 100)}%` }} />
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="commissions-card">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h2 className="font-bold text-zinc-950 text-base flex items-center gap-2">
                <CircleDollarSign size={16} /> Commissions
              </h2>
              <p className="text-xs text-zinc-500 mt-1">Pending: <span className="font-bold text-amber-700">{fmt(stats.pending_commission_usd)}</span> · Paid: <span className="font-bold text-emerald-700">{fmt(stats.paid_commission_usd)}</span></p>
            </div>
            <button
              onClick={requestPayout}
              disabled={!canRequestPayout || requestingPayout}
              data-testid="btn-request-payout"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${canRequestPayout && !requestingPayout ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-zinc-100 text-zinc-400 cursor-not-allowed"}`}>
              {requestingPayout ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {hasPendingRequest ? "Payout requested" : `Request payout${canRequestPayout ? ` · ${fmt(stats.pending_commission_usd)}` : ""}`}
            </button>
          </div>
          {hasPendingRequest && (
            <div className="mb-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="payout-request-notice">
              We received your payout request and will settle within 5 business days.
            </div>
          )}
          {commissions.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No commissions yet — once a client you referred pays, it shows up here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-100 text-zinc-500">
                  {["Date", "Project", "Paid by client", "Your cut", "Status"].map(h => <th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wide">{h}</th>)}
                </tr></thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.commission_id} className="border-b border-zinc-50" data-testid={`commission-${c.commission_id}`}>
                      <td className="py-2 px-3 text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="py-2 px-3 font-mono text-xs">{c.project_id}</td>
                      <td className="py-2 px-3">{fmt(c.paid_amount_usd)}</td>
                      <td className="py-2 px-3 font-bold text-emerald-700">{fmt(c.amount_usd)}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.status === "paid" ? "bg-emerald-100 text-emerald-700" : c.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="brand-kit-card">
          <h2 className="font-bold text-zinc-950 text-base mb-1 flex items-center gap-2">
            <Package size={16} /> Brand kit
          </h2>
          <p className="text-xs text-zinc-500 mb-4">
            Official FastPrototype.ai logos — please use them as-is on your blog, newsletter, or social posts. Do not modify the colors or proportions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="brand-kit-list">
            {[
              { src: "/brand/fastprototype-logo.png",     dl: "fastprototype-logo.png",     title: "Full logo (PNG)",       desc: "Color, transparent edges. Best for presentations & blog headers." },
              { src: "/brand/fastprototype-wordmark.svg", dl: "fastprototype-wordmark.svg", title: "Wordmark (SVG)",        desc: "Vector, scales perfectly. Use anywhere a wide logo fits." },
              { src: "/brand/fastprototype-mark.svg",     dl: "fastprototype-mark.svg",     title: "Mark only (SVG)",       desc: "Just the chevron. Use as a profile / favicon / social avatar." },
            ].map((asset) => (
              <div key={asset.dl} className="bg-zinc-50 rounded-2xl p-4 flex flex-col gap-2" data-testid={`brand-asset-${asset.dl}`}>
                <div className="bg-white rounded-xl border border-zinc-100 h-24 flex items-center justify-center px-3">
                  <img src={asset.src} alt={asset.title} className="max-h-16 max-w-full" />
                </div>
                <div className="font-semibold text-zinc-900 text-sm">{asset.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{asset.desc}</div>
                <a
                  href={asset.src}
                  download={asset.dl}
                  data-testid={`btn-download-brand-${asset.dl}`}
                  className="self-start mt-1 inline-flex items-center gap-1.5 bg-zinc-950 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-zinc-800"
                >
                  <Download size={11} /> Download
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="materials-card">
          <h2 className="font-bold text-zinc-950 text-base mb-1 flex items-center gap-2">
            <Package size={16} /> Materials
          </h2>
          <p className="text-xs text-zinc-500 mb-3">Logos, screenshots, pitch copy — approved for use.</p>
          {materials.length === 0 ? (
            <p className="text-sm text-zinc-500 py-3">No materials published yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="materials-list">
              {materials.map(m => (
                <div key={m.material_id} data-testid={`material-${m.material_id}`}
                  className="bg-zinc-50 rounded-2xl p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    {m.category === "screenshot" ? <ImageIcon size={14} className="text-zinc-600" /> : <FileText size={14} className="text-zinc-600" />}
                    <span className="font-bold text-zinc-950 text-sm flex-1">{m.title}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-white text-zinc-500 px-2 py-0.5 rounded-full">{m.category}</span>
                  </div>
                  {m.description && <div className="text-xs text-zinc-600">{m.description}</div>}
                  {m.body && <div className="text-xs text-zinc-700 whitespace-pre-wrap bg-white rounded-lg p-2 border border-zinc-200 max-h-40 overflow-y-auto">{m.body}</div>}
                  {m.file_id && (
                    <button onClick={() => downloadMaterial(m)} data-testid={`btn-download-material-${m.material_id}`}
                      className="self-start mt-1 inline-flex items-center gap-1.5 bg-zinc-950 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-zinc-800">
                      <Download size={11} /> {m.file_filename || "Download"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Guidelines open={openGuide} onClose={() => setOpenGuide(false)} guide={guide} />
    </div>
  );
}
